import type { Control, FieldPath, FieldValues } from 'react-hook-form';

import { Combobox, type ComboboxOption } from '@/components/ui/combobox';
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';

interface FormComboboxFieldProps<TFieldValues extends FieldValues> {
  control: Control<TFieldValues>;
  name: FieldPath<TFieldValues>;
  label: string;
  placeholder: string;
  searchPlaceholder?: string;
  description?: React.ReactNode;
  required?: boolean;
  options: ComboboxOption[];
  className?: string;
}

export const FormComboboxField = <TFieldValues extends FieldValues>({
  control,
  name,
  label,
  placeholder,
  searchPlaceholder,
  description,
  required,
  options,
  className,
}: FormComboboxFieldProps<TFieldValues>) => (
  <FormField
    control={control}
    name={name}
    render={({ field }) => (
      <FormItem className={className}>
        <FormLabel>
          {label} {required && <span className="text-primary">*</span>}
        </FormLabel>
        <FormControl>
          <Combobox
            options={options}
            value={field.value as string | undefined}
            onSelect={field.onChange}
            placeholder={placeholder}
            searchPlaceholder={searchPlaceholder}
          />
        </FormControl>
        {description && <FormDescription>{description}</FormDescription>}
        <FormMessage />
      </FormItem>
    )}
  />
);
FormComboboxField.displayName = 'FormComboboxField';
